/**
 * SupplierQualificationDialogs — "add/record" modals for the supplier
 * qualification dossier (SUP-Q1..Q4). Each posts to a real endpoint and then
 * invalidates the supplier-detail query so the relevant tab refreshes. These
 * fill the gap where the dossier (agreements, audits, risk, requalification,
 * questionnaires) was previously read-only / seed-only.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import dialog from '../AuthorWorkbench/NewDocumentForm.module.css';

function useInvalidateDetail(supplierId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['suppliers', supplierId, 'detail'] });
}

// ---------------------------------------------------------------------------
// SUP-Q1 — add a quality agreement / certificate (with expiry).
// ---------------------------------------------------------------------------
const AGREEMENT_TYPES = [
  'QUALITY_AGREEMENT', 'SUPPLY_AGREEMENT', 'GMP_CERTIFICATE', 'ISO_9001',
  'ISO_13485', 'GDP_CERTIFICATE', 'GDP_LICENSE', 'MANUFACTURING_LICENSE',
  'NDA', 'OTHER',
];

export function AddAgreementDialog({ supplierId, isOpen, onClose }: { supplierId: string; isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateDetail(supplierId);
  const [type, setType] = useState('QUALITY_AGREEMENT');
  const [title, setTitle] = useState('');
  const [reference, setReference] = useState('');
  const [effective, setEffective] = useState('');
  const [expires, setExpires] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation<{ agreement_id: string }, Error, void>({
    mutationFn: () => apiFetch<{ agreement_id: string }>(`/api/v1/suppliers/${supplierId}/quality-agreements`, {
      method: 'POST',
      body: {
        agreement_type: type,
        title: title.trim(),
        reference: reference.trim() || undefined,
        effective_at_utc: effective || undefined,
        expires_at_utc: expires || undefined,
      },
    }),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e) => setErr(e.message),
  });

  if (!isOpen) return null;
  const canSave = title.trim().length >= 2 && !save.isPending;
  return (
    <DialogShell titleId="add-agreement" onClose={onClose} disabled={save.isPending}
      title={t('supplier.detail.agreement.title', { defaultValue: 'Add quality agreement / certificate' })}
      desc={t('supplier.detail.agreement.sub', { defaultValue: 'Record a quality/supply agreement or a GMP/ISO certificate with its validity dates. Expiry monitoring is automatic.' })}>
      <form className={dialog.form} onSubmit={(e) => { e.preventDefault(); setErr(null); if (canSave) save.mutate(); }} noValidate>
        {err && <div role="alert" className={dialog.errorBanner}>{err}</div>}
        <Field label={t('supplier.detail.agreement.type', { defaultValue: 'Type' })} required>
          <select className={dialog.select} value={type} onChange={(e) => setType(e.target.value)}>
            {AGREEMENT_TYPES.map((x) => <option key={x} value={x}>{t(`supplier.detail.agreement.types.${x}`, { defaultValue: x.replace(/_/g, ' ') })}</option>)}
          </select>
        </Field>
        <Field label={t('supplier.detail.agreement.titleLabel', { defaultValue: 'Title' })} required>
          <input className={dialog.input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
            placeholder={t('supplier.detail.agreement.titlePh', { defaultValue: 'e.g. Quality Agreement 2026 / GMP Certificate' })} />
        </Field>
        <Field label={t('supplier.detail.agreement.reference', { defaultValue: 'Reference (optional)' })}>
          <input className={dialog.input} value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} />
        </Field>
        <div className={dialog.field} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ display: 'block' }}>
            <span className={dialog.label}>{t('supplier.detail.agreement.effective', { defaultValue: 'Effective date' })}</span>
            <input type="date" className={dialog.input} value={effective} onChange={(e) => setEffective(e.target.value)} />
          </label>
          <label style={{ display: 'block' }}>
            <span className={dialog.label}>{t('supplier.detail.agreement.expires', { defaultValue: 'Expiry date' })}</span>
            <input type="date" className={dialog.input} value={expires} onChange={(e) => setExpires(e.target.value)} />
          </label>
        </div>
        <DialogFooter onClose={onClose} pending={save.isPending} canSave={canSave}
          submitLabel={t('supplier.detail.agreement.save', { defaultValue: 'Add agreement' })} />
      </form>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------
// SUP-Q2 — schedule a supplier audit.
// ---------------------------------------------------------------------------
export function ScheduleAuditDialog({ supplierId, isOpen, onClose }: { supplierId: string; isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateDetail(supplierId);
  const [when, setWhen] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation<{ audit_id: string }, Error, void>({
    mutationFn: () => apiFetch<{ audit_id: string }>(`/api/v1/suppliers/${supplierId}/audits`, {
      method: 'POST', body: { scheduled_at_utc: when },
    }),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e) => setErr(e.message),
  });
  if (!isOpen) return null;
  const canSave = !!when && !save.isPending;
  return (
    <DialogShell titleId="schedule-audit" onClose={onClose} disabled={save.isPending}
      title={t('supplier.detail.audit.title', { defaultValue: 'Schedule supplier audit' })}
      desc={t('supplier.detail.audit.sub', { defaultValue: 'Schedule an audit for this supplier. You can record its outcome later from the Audits tab.' })}>
      <form className={dialog.form} onSubmit={(e) => { e.preventDefault(); setErr(null); if (canSave) save.mutate(); }} noValidate>
        {err && <div role="alert" className={dialog.errorBanner}>{err}</div>}
        <Field label={t('supplier.detail.audit.when', { defaultValue: 'Scheduled date' })} required>
          <input type="date" className={dialog.input} value={when} onChange={(e) => setWhen(e.target.value)} />
        </Field>
        <DialogFooter onClose={onClose} pending={save.isPending} canSave={canSave}
          submitLabel={t('supplier.detail.audit.save', { defaultValue: 'Schedule audit' })} />
      </form>
    </DialogShell>
  );
}

// SUP-Q2 — record the outcome of a scheduled/in-progress audit.
interface AuditOpt { audit_id: string; scheduled_for_utc?: string | null; status: string }
export function RecordAuditOutcomeDialog({ supplierId, audits, isOpen, onClose }: { supplierId: string; audits: AuditOpt[]; isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateDetail(supplierId);
  const open = audits.filter((a) => a.status === 'SCHEDULED' || a.status === 'IN_PROGRESS');
  const [auditId, setAuditId] = useState('');
  const [outcome, setOutcome] = useState('PASS');
  const [score, setScore] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation<{ audit_id: string }, Error, void>({
    mutationFn: () => apiFetch<{ audit_id: string }>(`/api/v1/suppliers/${supplierId}/audits/${auditId}/outcome`, {
      method: 'POST', body: { outcome, outcome_score: score.trim() !== '' ? Number(score) : undefined },
    }),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e) => setErr(e.message),
  });
  if (!isOpen) return null;
  const canSave = !!auditId && !save.isPending;
  return (
    <DialogShell titleId="audit-outcome" onClose={onClose} disabled={save.isPending}
      title={t('supplier.detail.auditOutcome.title', { defaultValue: 'Record audit outcome' })}
      desc={t('supplier.detail.auditOutcome.sub', { defaultValue: 'Close a scheduled audit with its outcome and score.' })}>
      <form className={dialog.form} onSubmit={(e) => { e.preventDefault(); setErr(null); if (!auditId) { setErr(t('supplier.detail.auditOutcome.pick', { defaultValue: 'Pick a scheduled audit.' })); return; } save.mutate(); }} noValidate>
        {err && <div role="alert" className={dialog.errorBanner}>{err}</div>}
        <Field label={t('supplier.detail.auditOutcome.audit', { defaultValue: 'Scheduled audit' })} required>
          <select className={dialog.select} value={auditId} onChange={(e) => setAuditId(e.target.value)}>
            <option value="">{open.length ? t('supplier.detail.auditOutcome.pickOpt', { defaultValue: '— Pick an audit —' }) : t('supplier.detail.auditOutcome.none', { defaultValue: 'No scheduled audits' })}</option>
            {open.map((a) => <option key={a.audit_id} value={a.audit_id}>{(a.scheduled_for_utc ? new Date(a.scheduled_for_utc).toLocaleDateString() : a.audit_id.slice(0, 8))} · {a.status}</option>)}
          </select>
        </Field>
        <Field label={t('supplier.detail.auditOutcome.outcome', { defaultValue: 'Outcome' })} required>
          <select className={dialog.select} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="PASS">{t('supplier.detail.auditOutcome.pass', { defaultValue: 'Pass' })}</option>
            <option value="CONDITIONAL">{t('supplier.detail.auditOutcome.conditional', { defaultValue: 'Conditional' })}</option>
            <option value="FAIL">{t('supplier.detail.auditOutcome.fail', { defaultValue: 'Fail' })}</option>
          </select>
        </Field>
        <Field label={t('supplier.detail.auditOutcome.score', { defaultValue: 'Score (0–100, optional)' })}>
          <input type="number" min={0} max={100} step="0.1" className={dialog.input} value={score} onChange={(e) => setScore(e.target.value)} />
        </Field>
        <DialogFooter onClose={onClose} pending={save.isPending} canSave={canSave}
          submitLabel={t('supplier.detail.auditOutcome.save', { defaultValue: 'Record outcome' })} />
      </form>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------
// SUP-Q3 — record a scored supplier risk assessment (stamps the supplier risk).
// ---------------------------------------------------------------------------
const RISK_TRIGGERS = ['INITIAL_QUALIFICATION', 'ANNUAL_REASSESSMENT', 'CAPA_TRIGGERED', 'FOR_CAUSE', 'MANUAL'];
export function RiskAssessmentDialog({ supplierId, isOpen, onClose }: { supplierId: string; isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateDetail(supplierId);
  const [sc, setSc] = useState('50');
  const [hq, setHq] = useState('80');
  const [rf, setRf] = useState('20');
  const [gr, setGr] = useState('30');
  const [trigger, setTrigger] = useState('MANUAL');
  const [err, setErr] = useState<string | null>(null);
  const n = (v: string) => (v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : NaN);
  const valid = [sc, hq, rf, gr].every((v) => { const x = n(v); return Number.isFinite(x) && x >= 0 && x <= 100; });
  const preview = valid ? Math.max(0, Math.min(100, n(sc) * 0.4 + (100 - n(hq)) * 0.3 + n(rf) * 0.2 + n(gr) * 0.1)) : null;
  const cat = preview == null ? '—' : preview >= 70 ? 'CRITICAL' : preview >= 50 ? 'MAJOR' : preview >= 25 ? 'MINOR' : 'LOW';
  const save = useMutation<{ computed_risk_category: string }, Error, void>({
    mutationFn: () => apiFetch<{ computed_risk_category: string }>(`/api/v1/suppliers/${supplierId}/risk-assessments`, {
      method: 'POST',
      body: {
        supply_criticality_score: n(sc), historical_quality_score: n(hq),
        regulatory_findings_score: n(rf), geographic_risk_score: n(gr),
        recompute_trigger: trigger,
      },
    }),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e) => setErr(e.message),
  });
  if (!isOpen) return null;
  const canSave = valid && !save.isPending;
  const scoreInput = (label: string, v: string, set: (s: string) => void, hint: string) => (
    <Field label={label}>
      <input type="number" min={0} max={100} step="1" className={dialog.input} value={v} onChange={(e) => set(e.target.value)} />
      <span style={{ fontSize: 12, color: 'var(--color-text-muted, #6B7989)' }}>{hint}</span>
    </Field>
  );
  return (
    <DialogShell titleId="risk-assessment" onClose={onClose} disabled={save.isPending}
      title={t('supplier.detail.risk.title', { defaultValue: 'Record risk assessment' })}
      desc={t('supplier.detail.risk.sub', { defaultValue: 'Score four factors (0–100). The weighted risk score updates the supplier’s risk category.' })}>
      <form className={dialog.form} onSubmit={(e) => { e.preventDefault(); setErr(null); if (canSave) save.mutate(); }} noValidate>
        {err && <div role="alert" className={dialog.errorBanner}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {scoreInput(t('supplier.detail.risk.sc', { defaultValue: 'Supply criticality' }), sc, setSc, t('supplier.detail.risk.scHint', { defaultValue: 'higher = more critical' }))}
          {scoreInput(t('supplier.detail.risk.hq', { defaultValue: 'Historical quality' }), hq, setHq, t('supplier.detail.risk.hqHint', { defaultValue: 'higher = better (lowers risk)' }))}
          {scoreInput(t('supplier.detail.risk.rf', { defaultValue: 'Regulatory findings' }), rf, setRf, t('supplier.detail.risk.rfHint', { defaultValue: 'higher = more findings' }))}
          {scoreInput(t('supplier.detail.risk.gr', { defaultValue: 'Geographic risk' }), gr, setGr, t('supplier.detail.risk.grHint', { defaultValue: 'higher = riskier region' }))}
        </div>
        <Field label={t('supplier.detail.risk.trigger', { defaultValue: 'Reassessment trigger' })}>
          <select className={dialog.select} value={trigger} onChange={(e) => setTrigger(e.target.value)}>
            {RISK_TRIGGERS.map((x) => <option key={x} value={x}>{t(`supplier.detail.risk.triggers.${x}`, { defaultValue: x.replace(/_/g, ' ') })}</option>)}
          </select>
        </Field>
        <div className={dialog.field} style={{ background: 'var(--color-surface-subtle, #F1F5F9)', borderRadius: 8, padding: '8px 12px' }}>
          {t('supplier.detail.risk.preview', { defaultValue: 'Computed risk score' })}: <strong>{preview == null ? '—' : preview.toFixed(1)}</strong> · <strong>{cat}</strong>
        </div>
        <DialogFooter onClose={onClose} pending={save.isPending} canSave={canSave}
          submitLabel={t('supplier.detail.risk.save', { defaultValue: 'Record assessment' })} />
      </form>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------
// SUP-Q4 — record a requalification activity.
// ---------------------------------------------------------------------------
export function RequalificationDialog({ supplierId, isOpen, onClose }: { supplierId: string; isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateDetail(supplierId);
  const [cycle, setCycle] = useState('');
  const [trigger, setTrigger] = useState('PERIODIC');
  const [due, setDue] = useState('');
  const [performed, setPerformed] = useState('');
  const [outcome, setOutcome] = useState('REQUALIFIED');
  const [freq, setFreq] = useState('12');
  const [nextDue, setNextDue] = useState('');
  const [summary, setSummary] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation<{ requalification_id: string }, Error, void>({
    mutationFn: () => apiFetch<{ requalification_id: string }>(`/api/v1/suppliers/${supplierId}/requalifications`, {
      method: 'POST',
      body: {
        cycle_label: cycle.trim(), trigger, due_at_utc: due,
        performed_at_utc: performed || undefined,
        outcome: performed ? outcome : undefined,
        frequency_months: freq.trim() !== '' ? Number(freq) : undefined,
        next_due_at_utc: nextDue || undefined,
        performance_summary: summary.trim() || undefined,
      },
    }),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e) => setErr(e.message),
  });
  if (!isOpen) return null;
  const canSave = cycle.trim().length >= 2 && !!due && !save.isPending;
  return (
    <DialogShell titleId="requal" onClose={onClose} disabled={save.isPending}
      title={t('supplier.detail.requal.title', { defaultValue: 'Record requalification' })}
      desc={t('supplier.detail.requal.sub', { defaultValue: 'Log a periodic/for-cause requalification. Leave "performed" empty to schedule it; set it to record a completed one with an outcome.' })}>
      <form className={dialog.form} onSubmit={(e) => { e.preventDefault(); setErr(null); if (canSave) save.mutate(); }} noValidate>
        {err && <div role="alert" className={dialog.errorBanner}>{err}</div>}
        <Field label={t('supplier.detail.requal.cycle', { defaultValue: 'Cycle label' })} required>
          <input className={dialog.input} value={cycle} onChange={(e) => setCycle(e.target.value)} maxLength={120} placeholder="2026 Periodic" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label={t('supplier.detail.requal.trigger', { defaultValue: 'Trigger' })}>
            <select className={dialog.select} value={trigger} onChange={(e) => setTrigger(e.target.value)}>
              {['INITIAL', 'PERIODIC', 'FOR_CAUSE', 'POST_CAPA'].map((x) => <option key={x} value={x}>{t(`supplier.detail.requal.triggers.${x}`, { defaultValue: x.replace(/_/g, ' ') })}</option>)}
            </select>
          </Field>
          <Field label={t('supplier.detail.requal.freq', { defaultValue: 'Frequency (months)' })}>
            <input type="number" min={0} className={dialog.input} value={freq} onChange={(e) => setFreq(e.target.value)} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label={t('supplier.detail.requal.due', { defaultValue: 'Due date' })} required>
            <input type="date" className={dialog.input} value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
          <Field label={t('supplier.detail.requal.performed', { defaultValue: 'Performed date (optional)' })}>
            <input type="date" className={dialog.input} value={performed} onChange={(e) => setPerformed(e.target.value)} />
          </Field>
        </div>
        {performed && (
          <Field label={t('supplier.detail.requal.outcome', { defaultValue: 'Outcome' })}>
            <select className={dialog.select} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              <option value="REQUALIFIED">{t('supplier.detail.requal.req', { defaultValue: 'Requalified' })}</option>
              <option value="REQUALIFIED_WITH_CONDITIONS">{t('supplier.detail.requal.reqCond', { defaultValue: 'Requalified with conditions' })}</option>
              <option value="NOT_REQUALIFIED">{t('supplier.detail.requal.notReq', { defaultValue: 'Not requalified' })}</option>
            </select>
          </Field>
        )}
        <Field label={t('supplier.detail.requal.nextDue', { defaultValue: 'Next due (optional)' })}>
          <input type="date" className={dialog.input} value={nextDue} onChange={(e) => setNextDue(e.target.value)} />
        </Field>
        <Field label={t('supplier.detail.requal.summary', { defaultValue: 'Performance summary (optional)' })}>
          <input className={dialog.input} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={1000} />
        </Field>
        <DialogFooter onClose={onClose} pending={save.isPending} canSave={canSave}
          submitLabel={t('supplier.detail.requal.save', { defaultValue: 'Record requalification' })} />
      </form>
    </DialogShell>
  );
}

// SUP-Q4 — record a qualification questionnaire / assessment.
export function QuestionnaireDialog({ supplierId, isOpen, onClose }: { supplierId: string; isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateDetail(supplierId);
  const [type, setType] = useState('SELF_ASSESSMENT');
  const [score, setScore] = useState('');
  const [recommendation, setRecommendation] = useState('APPROVE');
  const [status, setStatus] = useState('ACCEPTED');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation<{ questionnaire_id: string }, Error, void>({
    mutationFn: () => apiFetch<{ questionnaire_id: string }>(`/api/v1/suppliers/${supplierId}/assessments`, {
      method: 'POST',
      body: {
        assessment_type: type, status,
        overall_score: score.trim() !== '' ? Number(score) : undefined,
        recommendation: recommendation || undefined,
      },
    }),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e) => setErr(e.message),
  });
  if (!isOpen) return null;
  const canSave = !save.isPending;
  return (
    <DialogShell titleId="questionnaire" onClose={onClose} disabled={save.isPending}
      title={t('supplier.detail.questionnaire.title', { defaultValue: 'Record qualification questionnaire' })}
      desc={t('supplier.detail.questionnaire.sub', { defaultValue: 'Capture a qualification questionnaire / assessment result for this supplier.' })}>
      <form className={dialog.form} onSubmit={(e) => { e.preventDefault(); setErr(null); if (canSave) save.mutate(); }} noValidate>
        {err && <div role="alert" className={dialog.errorBanner}>{err}</div>}
        <Field label={t('supplier.detail.questionnaire.type', { defaultValue: 'Assessment type' })}>
          <select className={dialog.select} value={type} onChange={(e) => setType(e.target.value)}>
            {['SELF_ASSESSMENT', 'ON_SITE', 'DESKTOP', 'POSTAL', 'GENERAL'].map((x) => <option key={x} value={x}>{t(`supplier.detail.questionnaire.types.${x}`, { defaultValue: x.replace(/_/g, ' ') })}</option>)}
          </select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label={t('supplier.detail.questionnaire.score', { defaultValue: 'Overall score (0–100)' })}>
            <input type="number" min={0} max={100} step="1" className={dialog.input} value={score} onChange={(e) => setScore(e.target.value)} />
          </Field>
          <Field label={t('supplier.detail.questionnaire.status', { defaultValue: 'Status' })}>
            <select className={dialog.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {['ISSUED', 'RECEIVED', 'ACCEPTED', 'REJECTED', 'WITHDRAWN'].map((x) => <option key={x} value={x}>{t(`supplier.detail.questionnaire.statuses.${x}`, { defaultValue: x })}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t('supplier.detail.questionnaire.recommendation', { defaultValue: 'Recommendation' })}>
          <select className={dialog.select} value={recommendation} onChange={(e) => setRecommendation(e.target.value)}>
            {['APPROVE', 'APPROVE_WITH_CONDITIONS', 'REJECT'].map((x) => <option key={x} value={x}>{t(`supplier.detail.questionnaire.recs.${x}`, { defaultValue: x.replace(/_/g, ' ') })}</option>)}
          </select>
        </Field>
        <DialogFooter onClose={onClose} pending={save.isPending} canSave={canSave}
          submitLabel={t('supplier.detail.questionnaire.save', { defaultValue: 'Record questionnaire' })} />
      </form>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------
// Shared shell + small field helpers (keeps each dialog terse).
// ---------------------------------------------------------------------------
function DialogShell({ titleId, title, desc, onClose, disabled, children }: {
  titleId: string; title: string; desc: string; onClose: () => void; disabled?: boolean; children: ReactNode;
}) {
  return (
    <div className={dialog.backdrop} role="presentation" onClick={(e) => { if (e.target === e.currentTarget && !disabled) onClose(); }}>
      <div className={dialog.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className={dialog.header}>
          <h2 id={titleId} className={dialog.title}>{title}</h2>
          <p className={dialog.desc}>{desc}</p>
        </header>
        {children}
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div className={dialog.field}>
      <label className={dialog.label}>{label}{required && <span aria-hidden="true" className={dialog.required}>*</span>}</label>
      {children}
    </div>
  );
}

function DialogFooter({ onClose, pending, canSave, submitLabel }: { onClose: () => void; pending: boolean; canSave: boolean; submitLabel: string }) {
  const { t } = useTranslation();
  return (
    <footer className={dialog.footer}>
      <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
      <Button type="submit" variant="primary" isLoading={pending} disabled={!canSave}>{submitLabel}</Button>
    </footer>
  );
}
